import React, { useCallback, useEffect, useState } from 'react';
import { isStandalone } from '../../function-utils/device-location';
import styled from '@emotion/styled';
import type { Variants } from 'framer-motion';
import {
  palette,
  spacing,
  radius,
  shadow,
  typography,
  zIndex,
  transition,
  duration,
  easing,
  gradient,
  layout,
} from '../../style-utils/theme';
import { LogoMark } from '../logo/logo-mark';
import { CloseIcon } from '../icons/close-icon';
import { down } from '../../style-utils/media-queries';
import { DISTANCE, Presence, T, m } from '../../motion';

/** The Chrome/Edge/Android install event (not in the standard DOM lib types). */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

/* Dismissing holds the banner back for a day — no snooze-and-nag loop. */
const DISMISS_COOLDOWN_MS = 24 * 60 * 60 * 1000;
const DISMISS_KEY = 'lobbero-install-dismissed-at';

/* Running as an installed PWA — the banner must never appear. `isStandalone`
   lives in function-utils/device-location now: the location ask needs the same
   answer to word "turn it back on" without pointing at an address bar. */

const isIOS = (): boolean =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) &&
  !(window.navigator as unknown as { MSStream?: unknown }).MSStream;

const wasRecentlyDismissed = (): boolean => {
  try {
    const at = Number(window.localStorage.getItem(DISMISS_KEY) || 0);

    return Date.now() - at < DISMISS_COOLDOWN_MS;
  } catch {
    return false;
  }
};

const rememberDismissal = (): void => {
  try {
    window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    /* private mode — session-only dismissal is fine */
  }
};

/* Slim strip pinned to the top edge — frosted surface, hairline underline.
 *
 * `dropdown`, NOT `toast`, and the difference is visible. At `toast` (3000) this
 * strip outranks every modal scrim (`overlay` 2000 / `modal` 2100), so while it
 * was showing it painted bright and clickable across the top edge of an otherwise
 * dimmed page — the same symptom as an undimmed header, and one no portal can fix
 * since this is already a body-level app-root child with no trapping ancestor.
 *
 * `toast` is reserved for transient confirmations that must be readable even from
 * inside a dialog. This is a persistent banner, not a confirmation, so it belongs
 * above page content and below any layer. */
const Bar = m.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: ${zIndex.dropdown};
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  /* The status bar overlays the top edge in an installed PWA — which is the
     one context where this banner is still on screen at all, since it only
     shows BEFORE installing on iOS but stays up on a launched-standalone
     Android window. Grow the bar by the inset and push its content below it,
     rather than moving the bar down and leaving a translucent strip above. */
  /* 56px (seven grid steps): it was 52, off the 8-point grid, and the body
     padding in index.css mirrors this number — change both together. */
  height: calc(56px + env(safe-area-inset-top));
  /* IT LANDED ON TOP OF THE BELL. Measured on every touch shape by
     scripts/analytics/responsive.js: this banner's Dismiss button at
     (260, 4, 44, 44) over "Notifications, 9 unread" at (268, 8, 44, 44) — a
     36x40px overlap — and elementFromPoint at the bell's own centre returning
     this banner. On a phone, while the banner was up, neither the bell nor the
     account menu could be tapped at all.
     layout.bellDockReserve is the token every other top-anchored header uses
     for exactly this, asserted in app-surfaces.test.ts. The dock is fixed and
     in no banner's flow, so the room has to be RESERVED rather than laid out.
     NO BACKTICKS IN HERE: this comment is inside a tagged template literal, and
     one closes the CSS string mid-sentence. */
  padding: env(safe-area-inset-top)
    calc(${spacing.md} + ${layout.bellDockReserve}) 0 ${spacing.md};
  background: ${palette.surfaceGlass};
  backdrop-filter: blur(10px) saturate(1.4);
  -webkit-backdrop-filter: blur(10px) saturate(1.4);
  border-bottom: 1px solid ${palette.line};
  box-shadow: ${shadow.sm};

  ${down('sm')} {
    gap: ${spacing.xs};
    /* Restates the top inset: a bare shorthand here would reset it to 0 and
       put the bar back under the status bar at exactly the width where
       phones live. */
    /* The phone override reset the reserve away again, which is exactly
       where the overlap was measured. The gutter narrows; the room the
       bell needs does not. */
    padding: env(safe-area-inset-top)
      calc(${spacing.sm} + ${layout.bellDockReserve}) 0 ${spacing.sm};
  }
`;

const Copy = styled.p`
  flex: 1;
  min-width: 0;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.ink};

  & b {
    font-weight: ${typography.weight.semibold};
  }

  & span {
    color: ${palette.slate};

    ${down('sm')} {
      display: none;
    }
  }
`;

const Install = styled.button`
  flex-shrink: 0;
  height: ${layout.buttonHeight};
  padding: 0 ${spacing.sm};
  border: none;
  border-radius: ${radius.pill};
  background: ${gradient.primary};
  color: ${palette.white};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.semibold};
  cursor: pointer;
  transition: ${transition.colors}, transform ${duration.fast} ${easing.spring};

  &:hover {
    background: ${gradient.primaryHover};
    transform: translateY(-1px);
  }
  &:active {
    transform: scale(0.97);
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

const Dismiss = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${layout.touchTarget};
  height: ${layout.touchTarget};
  padding: 0;
  border: none;
  border-radius: ${radius.circle};
  background: transparent;
  cursor: pointer;
  transition: ${transition.colors};

  & svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    background: ${palette.surfaceMuted};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

/* Slides in from the top edge it is pinned to, and back out the same way when
   dismissed. Opacity and y only: the strip pads the document via
   html.has-install-banner, and a scale or a height here would fight that. */
const banner: Variants = {
  hidden: { opacity: 0, y: -DISTANCE.medium, transition: T.exit },
  visible: { opacity: 1, y: 0, transition: T.enterFast },
};

export const InstallPrompt: React.FC = () => {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(
    null
  );
  const [visible, setVisible] = useState(false);
  const [ios, setIos] = useState(false);

  useEffect(() => {
    // Installed PWA (or launched standalone): never listen, never show.
    if (isStandalone()) return;

    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
      if (!wasRecentlyDismissed()) setVisible(true);
    };
    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);

    // iOS Safari never fires beforeinstallprompt — nudge with manual steps.
    if (isIOS() && !wasRecentlyDismissed()) {
      setIos(true);
      setVisible(true);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const dismiss = useCallback(() => {
    setVisible(false);
    rememberDismissal();
  }, []);

  const install = useCallback(async () => {
    if (!deferred) return;
    try {
      await deferred.prompt();
      await deferred.userChoice;
    } catch {
      /* ignore — nothing more we can do with a consumed prompt */
    } finally {
      // The captured prompt is single-use; declining the browser dialog
      // counts as a dismissal (cooldown) instead of re-nagging.
      setDeferred(null);
      setVisible(false);
      rememberDismissal();
    }
  }, [deferred]);

  // Nothing to show if installed/standalone, or (on Chrome) no install event.
  /* IT OVERLAID THE HEADER AND SWALLOWED IT.
     Measured on every touch shape: this banner is `position: fixed; top: 0;
     left: 0; right: 0`, so its box covered the notification bell and the
     account menu underneath — elementFromPoint at the bell's own centre
     returned the banner, and its Dismiss control was drawn on top of the
     account avatar. Reserving padding moves the banner's CONTENT and not its
     box, so the room has to come from the page instead: while the banner is up
     the document is pushed down by its height, and the class is removed the
     moment it is dismissed or unmounted. */
  const showing = visible && !isStandalone() && (ios || !!deferred);

  useEffect(() => {
    const root = document.documentElement;
    if (showing) root.classList.add('has-install-banner');
    else root.classList.remove('has-install-banner');

    return () => root.classList.remove('has-install-banner');
  }, [showing]);

  return (
    <Presence>
      {showing ? (
    <Bar
      key="install-banner"
      variants={banner}
      initial="hidden"
      animate="visible"
      exit="hidden"
      role="region"
      aria-label="Install Lobbero"
    >
      <LogoMark size={24} aria-hidden="true" />
      <Copy>
        <b>Install Lobbero</b>{' '}
        <span>
          {ios
            ? '— tap Share, then “Add to Home Screen”.'
            : '— faster, full-screen, always a tap away.'}
        </span>
      </Copy>
      {!ios && (
        <Install type="button" onClick={install}>
          Install
        </Install>
      )}
      <Dismiss
        type="button"
        onClick={dismiss}
        aria-label="Dismiss install banner"
      >
        <CloseIcon fill={palette.slate} />
      </Dismiss>
    </Bar>
      ) : null}
    </Presence>
  );
};
